/**
 * L5 perf — 카드·히어로 <img> 의 반응형 srcset(순수 · DB 0 · 네트워크 0).
 *
 * 어떤 그림에 후보를 만드나(비용 원칙 — Supabase 이미지 변환은 «원본 장수» 로 과금된다 · lib/apt/hero-srcset 머리말):
 *   · Storage 공개 객체(`/media/{bucket}/…` 프록시 또는 `https://{proj}.supabase.co/storage/v1/object/public/…`) 중
 *     시행사·공식 사진 버킷(apt-covers)만 → `/storage/v1/render/image/public/…?width=W&quality=75` 사다리 + 원본.
 *     (apt-covers 는 이미 hero-srcset 이 같은 변환을 쓰는 집합 — 새로 과금 대상이 되는 원본은 없다.)
 *   · 생성 이미지(/img/… = gen-images 파생본 고정 크기) · 위성(/img-sat) · 합성 카드(/api/og*·/media/og) · 외부 핫링크 → null.
 *     파생본은 비율이 다른 변형(1:1·1.9:1)뿐이라 같은 비율의 작은 판이 없다(cover 크롭이 달라져 화면이 바뀐다) — 후보 없음.
 *   · null 이면 호출부는 종전 <img src> 그대로(마크업 바이트 동일).
 *
 * ⛔ 폭 사다리는 원본 폭 «미만» 만. 마지막 후보는 항상 원본 URL(브라우저가 큰 화면에서 원본을 고른다).
 */
import { SUPABASE_PROJECT_HOST } from '@/lib/cl-core/config';

export const CARD_WIDTHS = [320, 640, 960, 1280] as const;

/** 목록 카드(.ds2-card__img · 4열 1160 → 약 270px · ≤760 2열). */
export const CARD_SIZES = '(max-width: 760px) 48vw, 280px';
/** 대표 현장 카드(.ds2-feat · 2단 hero 우측 ≈ 520px · ≤760 1단 전폭). */
export const FEATURE_SIZES = '(max-width: 760px) calc(100vw - 40px), 520px';

const OBJECT_SEG = '/storage/v1/object/public/';
const RENDER_SEG = '/storage/v1/render/image/public/';
const MEDIA_PREFIX = '/media/';
/** 변환을 허용하는 버킷(과금 집합 불변 — hero-srcset 과 같은 apt-covers 만). */
const ALLOW_BUCKETS = ['apt-covers/'];

interface StorageRef { origin: string; path: string; search: string }

/** Storage 공개 객체 참조로 풀어낸다. 아니면 null. */
export function storageRefOf(src: string): StorageRef | null {
  const s = String(src ?? '').trim();
  if (!s) return null;
  if (s.startsWith(MEDIA_PREFIX)) {
    const [p, q = ''] = s.slice(MEDIA_PREFIX.length).split('?');
    if (!p || p.startsWith('og/')) return null;
    return { origin: `https://${SUPABASE_PROJECT_HOST}`, path: p, search: q ? `?${q}` : '' };
  }
  let u: URL;
  try { u = new URL(s); } catch { return null; }
  if (!/\.supabase\.co$/i.test(u.hostname) || !u.pathname.startsWith(OBJECT_SEG)) return null;
  return { origin: u.origin, path: u.pathname.slice(OBJECT_SEG.length), search: u.search };
}

/**
 * srcset 문자열 또는 null. originalWidth 를 모르면 호출부의 레이아웃 폭(width 속성)이 아니라 «그림 원본 폭» 을 넘긴다 —
 * 모르면 기본 1600(apt-covers 실측 대표 폭 · hero-srcset 과 같은 기본값).
 */
export function cardSrcSet(src: string, originalWidth = 1600, widths: readonly number[] = CARD_WIDTHS): string | null {
  const ref = storageRefOf(src);
  if (!ref) return null;
  if (!ALLOW_BUCKETS.some((b) => ref.path.startsWith(b))) return null;
  const ladder = widths.filter((w) => w < originalWidth);
  if (!ladder.length) return null;
  const parts = ladder.map((w) => {
    const q = new URLSearchParams(ref.search);
    q.set('width', String(w));
    q.set('quality', '75');
    return `${ref.origin}${RENDER_SEG}${ref.path}?${q.toString()} ${w}w`;
  });
  parts.push(`${src} ${originalWidth}w`);
  return parts.join(', ');
}
